import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getVisitorId } from "src/lib/fingerprint";
import { useLocalStorage } from "usehooks-ts";

import { AVAILIO_LOGO } from "@constants/assets";
import { CONSTANTS } from "@constants/index";
import { STORAGE_KEYS } from "@constants/Kiosk";
import { kioskRouteEnum } from "@enums/route.enum";
import { useOnlineStatus } from "@hooks/useOnlineStatus/useOnlineStatus";
import { KioskSetupFormData } from "@models/components/kiosk.model";
import API_SERVICES from "@services/index";
import commonUtils from "@utils/common.utils";
import { formatDeviceId } from "@utils/kiosk.utils";

const KioskSetup: FC = () => {
  const { t } = useTranslation();
  const { isOnline } = useOnlineStatus();
  const navigator = useNavigate();

  const [deviceId, setDeviceId] = useLocalStorage(
    STORAGE_KEYS.KIOSK_DEVICE_ID,
    ""
  );

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
  } = useForm<KioskSetupFormData>({
    mode: "onSubmit",
  });

  useEffect(() => {
    if (deviceId) {
      navigator(kioskRouteEnum.Booking);
    }
  }, [deviceId, navigator]);

  const handleDeviceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDeviceId(e.target.value);
    setValue("deviceId", formatted, { shouldValidate: true });
  };

  const onSubmit = (data: KioskSetupFormData) => {
    getVisitorId()
      .then(response => {
        return API_SERVICES.KIOSK.CONFIGURE(data.deviceId, {
          components: response.components,
          visitorId: response.visitorId,
        });
      })
      .then(response => {
        if (!response.ok) {
          const errorMessage = response.data && response.data.message;
          commonUtils.toastService(errorMessage, CONSTANTS.TOAST_TYPE.ERROR);
          return;
        }

        setDeviceId(data.deviceId);
        navigator(kioskRouteEnum.Booking);
      })
      .catch(error => {
        console.error("Error setting up kiosk:", error);

        commonUtils.toastService(
          t("kiosk.login.unexpectedError"),
          CONSTANTS.TOAST_TYPE.ERROR
        );
      });
  };

  return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="card kiosk-login-card p-5">
        <div className="text-center mb-4">
          <img
            src={AVAILIO_LOGO}
            className="fg-logo-img kiosk-logo"
            alt={t("kiosk.login.logoAlt")}
          />
        </div>

        <h1 className="text-center fw-bold mb-3 kiosk-title">
          {t("kiosk.login.title")}
        </h1>
        <hr />

        <form onSubmit={handleSubmit(onSubmit)}>
          <p className="text-muted text-center mb-3 kiosk-instruction">
            {t("kiosk.login.instruction")}
          </p>
          <input
            type="text"
            id="deviceId"
            className={`form-control text-center mb-1 kiosk-device-input ${
              errors.deviceId ? "is-invalid" : ""
            }`}
            placeholder={t("kiosk.login.deviceIdPlaceholder")}
            maxLength={14}
            {...register("deviceId", {
              minLength: {
                message: t("kiosk.login.deviceIdMinLength"),
                value: 14,
              },
              pattern: {
                message: t("kiosk.login.deviceIdPattern"),
                value: /^[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/,
              },
              required: t("kiosk.login.deviceIdRequired"),
            })}
            onChange={handleDeviceIdChange}
          />
          {errors.deviceId && (
            <div className="invalid-feedback d-block text-center mb-3 kiosk-error-message">
              {errors.deviceId.message}
            </div>
          )}
          {!errors.deviceId && <div className="mb-3"></div>}

          {/* Button */}
          <div className="d-grid mb-4">
            <button
              type="submit"
              className="btn btn-primary btn-lg kiosk-confirm-btn"
              disabled={isSubmitting || !isOnline}
            >
              {isSubmitting
                ? t("kiosk.login.confirming")
                : t("kiosk.login.confirm")}
            </button>
          </div>
        </form>

        <hr />

        {/* Footer */}
        <div className="d-flex justify-content-between small text-muted">
          <span>
            {t("kiosk.login.networkLabel")}:{" "}
            <span
              data-testid="network-status"
              className={isOnline ? "text-success" : "text-danger"}
            >
              ●
            </span>{" "}
            {isOnline
              ? t("kiosk.login.networkConnected")
              : t("kiosk.login.networkDisconnected")}
          </span>
          <span>
            {t("kiosk.login.supportLabel")}:{" "}
            <span className="text-primary fw-bold" data-testid="support-email">
              support@fairgate.ch
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default KioskSetup;
